/**
 * +atmos: painted cumulus. Big soft stylised clouds as camera-following billboards on the sky (one
 * instanced draw): each card's shape is a smooth union of round puffs over a flat base (clean,
 * crisp silhouette, no noise holes), shaded like a lit volume: white sunlit tops, blue-grey bellies,
 * a silver lining toward the sun, hazier near the horizon, plus a bank of small flat clouds low
 * down. They drift slowly around the sky. Drawn at the far plane: every mountain and hill is in front.
 */
import * as THREE from 'three';
import { U } from '../../style';
import { ATMOS } from './uniforms';

export interface CloudUniforms {
  uCloud: { value: THREE.Color };
  uShade: { value: THREE.Color };
  uHorizon: { value: THREE.Color };
  uNight: { value: number };
}

export function buildClouds(colors: { cloud: number; shade: number; horizon: number }, cover = 0.65, seed0 = 7): { mesh: THREE.Mesh; u: CloudUniforms; dispose(): void } {
  let seed = seed0 * 7919 + 1;
  const rnd = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  type C = { az: number; el: number; w: number; a: number; s: number };
  const list: C[] = [];
  const big = Math.round(12 + 16 * cover);
  for (let i = 0; i < big; i++) {
    const az = ((i + rnd() * 0.7) / big) * Math.PI * 2;
    list.push({ az, el: 0.09 + Math.pow(rnd(), 1.6) * 0.4, w: 0.22 + rnd() * 0.26, a: 0.42 + rnd() * 0.2, s: rnd() * 100 });
  }
  const bank = Math.round(10 + 10 * cover);
  for (let i = 0; i < bank; i++) {
    const az = ((i + rnd()) / bank) * Math.PI * 2;
    list.push({ az, el: 0.035 + rnd() * 0.07, w: 0.14 + rnd() * 0.18, a: 0.26 + rnd() * 0.12, s: rnd() * 100 });
  }
  // Low (distant) first, high (overhead, nearer) last: later cards cover earlier ones.
  list.sort((p, q) => p.el - q.el);
  const quad = new THREE.PlaneGeometry(2, 1, 1, 1).translate(0, 0.5, 0);
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = quad.index;
  geo.setAttribute('position', quad.attributes['position']!);
  const a1 = new Float32Array(list.length * 4);
  const a2 = new Float32Array(list.length);
  list.forEach((c, i) => {
    a1.set([c.az, c.el, c.w, c.a], i * 4);
    a2[i] = c.s;
  });
  geo.setAttribute('aCloud', new THREE.InstancedBufferAttribute(a1, 4));
  geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(a2, 1));
  geo.instanceCount = list.length;
  const u: CloudUniforms = {
    uCloud: { value: new THREE.Color(colors.cloud) },
    uShade: { value: new THREE.Color(colors.shade) },
    uHorizon: { value: new THREE.Color(colors.horizon) },
    uNight: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms: { ...u, uTime: U.uTime, uSun: ATMOS.uSunDir, uSunCol: ATMOS.uSunColor },
    vertexShader: `attribute vec4 aCloud; attribute float aSeed; uniform float uTime;
      varying vec2 vP; varying float vSeed; varying float vEl; varying vec3 vDir; varying vec3 vRight; varying vec3 vUp;
      void main(){
        float az = aCloud.x + uTime * 0.0016 * (0.7 + 0.6 * fract(aSeed * 7.13));
        float el = aCloud.y;
        vec3 dir = vec3(cos(el) * cos(az), sin(el), -cos(el) * sin(az));
        vec3 right = normalize(cross(dir, vec3(0.0, 1.0, 0.0)));
        vec3 up = cross(right, dir);
        float R = 300.0;
        float w = R * tan(aCloud.z * 0.5);
        float hgt = w * aCloud.w * 2.4;
        // The card is a bit bigger than the shape (no billow ever touches its edge).
        vP = vec2(position.x * 1.45, position.y * 1.4 - 0.06);
        vec3 wp = cameraPosition + dir * R + right * vP.x * w + up * (vP.y / 1.2 * hgt - hgt * 0.12);
        vSeed = aSeed; vEl = el; vDir = dir; vRight = right; vUp = up;
        vec4 p = projectionMatrix * viewMatrix * vec4(wp, 1.0);
        gl_Position = p.xyww;
      }`,
    fragmentShader: `uniform vec3 uCloud; uniform vec3 uShade; uniform vec3 uHorizon; uniform vec3 uSun; uniform vec3 uSunCol; uniform float uNight;
      varying vec2 vP; varying float vSeed; varying float vEl; varying vec3 vDir; varying vec3 vRight; varying vec3 vUp;
      float h1(float n){ return fract(sin(n * 91.345 + vSeed * 12.9898) * 43758.5453); }
      float smin(float a, float b, float k){ float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0); return mix(b, a, h) - k * h * (1.0 - h); }
      void main(){
        vec2 p = vP;
        const float base = 0.16;
        float d = 1e3; float best = 1e3; vec2 bc = vec2(0.0); float br = 1.0;
        // A row of billows, tallest in the middle, plus two towers on top.
        for (int i = 0; i < 9; i++) {
          float fi = float(i);
          vec2 c; float r;
          if (i < 7) {
            float t = fi / 6.0;
            float bell = sin(t * 3.14159);
            r = (0.17 + 0.3 * bell) * (0.78 + 0.44 * h1(fi));
            c = vec2(mix(-0.74, 0.74, t) + (h1(fi + 9.0) - 0.5) * 0.16, base + r * 0.62 + 0.06 * bell * h1(fi + 3.0));
          } else {
            float side = fi == 7.0 ? -1.0 : 1.0;
            r = 0.18 + 0.1 * h1(fi);
            c = vec2(side * (0.1 + 0.22 * h1(fi + 5.0)), base + 0.42 + 0.22 * h1(fi + 2.0));
            if (h1(fi + 11.0) < 0.35) r *= 0.4;
          }
          float sd = length(p - c) - r;
          d = smin(d, sd, 0.07);
          if (sd < best) { best = sd; bc = c; br = r; }
        }
        // Flat base (softly rounded into the billows).
        float bottom = base - p.y;
        d = -smin(-d, -bottom, 0.05);
        float aa = fwidth(d) + 0.003;
        float alpha = 1.0 - smoothstep(-aa, aa, d);
        if (alpha <= 0.002) discard;
        // Volume shading: each billow lit as a sphere, plus a top-down gradient.
        vec2 n2 = (p - bc) / br;
        float l2 = dot(n2, n2);
        vec3 n3 = normalize(vec3(n2 / max(1.0, sqrt(l2)), sqrt(max(0.0, 1.0 - min(l2, 1.0))) + 0.15));
        vec3 sun = normalize(uSun);
        vec3 sL = normalize(vec3(dot(sun, vRight), max(dot(sun, vUp), -0.2) + 0.35, dot(sun, -vDir) * 0.6 + 0.4));
        float lit = dot(n3, sL) * 0.5 + 0.5;
        float g = smoothstep(base, base + 0.5, p.y);
        float t = clamp(lit * 0.7 + g * 0.62 - 0.22, 0.0, 1.0);
        float s1 = smoothstep(0.3, 0.52, t);
        float s2 = smoothstep(0.6, 0.78, t);
        vec3 mid = mix(uShade, uCloud, 0.66);
        vec3 belly = mix(uShade, uCloud, 0.18);
        vec3 col = mix(belly, mix(mid, uCloud, s2), s1);
        // Silver lining: the rim lights up, most when the sun is behind the cloud.
        float rim = 1.0 - smoothstep(0.0, 0.035, -d);
        float back = pow(max(dot(vDir, sun), 0.0), 3.0);
        col += uSunCol * rim * (0.1 + 0.5 * back) * (0.4 + 0.6 * g) * (1.0 - 0.7 * uNight);
        // Hazier and warmer toward the horizon.
        col = mix(col, uHorizon, (1.0 - smoothstep(0.02, 0.32, vEl)) * 0.42);
        gl_FragColor = vec4(col, alpha);
      }`,
    transparent: true,
    depthWrite: false,
  });
  mat.name = 'sky-clouds';
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'sky-clouds';
  mesh.frustumCulled = false;
  mesh.renderOrder = -9;
  return {
    mesh,
    u,
    dispose() {
      mesh.removeFromParent();
      quad.dispose();
      geo.dispose();
      mat.dispose();
    },
  };
}
